import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle, User, Mail, MapPin, CreditCard, Phone, AlertCircle } from 'lucide-react';
import TopHeader from './components/TopHeader';
import { bookingApi as apiService } from '../services/BookingService';

export default function BookingPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [frequency, setFrequency] = useState('one-time');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: 'ottawa',
    zip: '',
  });
  const [bookingComplete, setBookingComplete] = useState(false);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [checkingDates, setCheckingDates] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState(new Set());

  // Load services and providers on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load available time slots when date is selected
  useEffect(() => {
    if (selectedDate && selectedService && selectedProvider) {
      loadAvailableTimeSlots();
    }
  }, [selectedDate, selectedService, selectedProvider]);

  // Check availability for visible dates in the calendar
  useEffect(() => {
    if (selectedService && selectedProvider && currentStep === 2) {
      checkCalendarAvailability();
    }
  }, [currentMonth, selectedService, selectedProvider, currentStep]);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [servicesData, providersData] = await Promise.all([
        apiService.getServices(),
        apiService.getProviders(),
      ]);

      setServices(servicesData.map(itm => ({...itm, extra: JSON.parse(itm.description)})));
      setProviders(providersData);

      // Auto-select first provider if available
      if (providersData.length > 0) {
        setSelectedProvider(providersData[0]);
      }
    } catch (err) {
      setError('Failed to load services. Please try again later.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableTimeSlots = async () => {
    if (!selectedProvider || !selectedService || !selectedDate) return;

    setIsLoading(true);
    setError(null);
    try {
      const availabilities = await apiService.getAvailabilities(
        selectedProvider.id,
        selectedService.id,
        selectedDate
      );

      // Transform availabilities array into time slot objects
      const timeSlots = availabilities.map((time) => ({
        time: time,
        available: true,
      }));

      setAvailableTimeSlots(timeSlots);
    } catch (err) {
      setError('Failed to load available time slots.');
      console.error(err);
      setAvailableTimeSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a date is a working day based on provider's working plan
  const isWorkingDay = (date) => {
    if (!selectedProvider || !selectedProvider.settings || !selectedProvider.settings.workingPlan) {
      return true; // If no working plan, assume all days are available
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    const workingPlan = selectedProvider.settings.workingPlan;

    // Check if this day is in the working plan and not null
    return workingPlan[dayName] !== null && workingPlan[dayName] !== undefined;
  };

  // Check if a date has an exception in the working plan
  const hasWorkingPlanException = (date) => {
    if (!selectedProvider || !selectedProvider.settings || !selectedProvider.settings.workingPlanExceptions) {
      return null;
    }

    const dateStr = apiService.formatDate(date);
    return selectedProvider.settings.workingPlanExceptions[dateStr] || null;
  };

  // Check availability for all visible dates in the calendar
  const checkCalendarAvailability = async () => {
    if (!selectedProvider || !selectedService) return;

    const calendar = generateCalendar();
    const unavailable = new Set();

    // First, mark non-working days based on working plan
    calendar.forEach((date) => {
      if (date) {
        // Check if it's a working day
        const exception = hasWorkingPlanException(date);
        
        if (exception) {
          // If there's an exception, the date is available
          return;
        }
        
        if (!isWorkingDay(date)) {
          unavailable.add(date.toDateString());
        }
      }
    });

    setUnavailableDates(unavailable);

    // Optionally: Check actual availability via API for working days
    // This is commented out to avoid too many API calls
    // You can enable this for more accurate availability checking
    /*
    const workingDates = calendar.filter(date => 
      date && !unavailable.has(date.toDateString())
    );

    // Check availability in batches to avoid overwhelming the API
    const batchSize = 5;
    for (let i = 0; i < workingDates.length; i += batchSize) {
      const batch = workingDates.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (date) => {
          try {
            const availabilities = await apiService.getAvailabilities(
              selectedProvider.id,
              selectedService.id,
              date
            );

            if (!availabilities || availabilities.length === 0) {
              unavailable.add(date.toDateString());
            }
          } catch (err) {
            console.error('Error checking availability for', date, err);
          }
        })
      );
    }

    setUnavailableDates(new Set(unavailable));
    */
  };

  // Generate calendar for current month
  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dates = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      dates.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      if (date >= today) {
        dates.push(date);
      } else {
        dates.push(null);
      }
    }
    
    return dates;
  };

  const calendar = generateCalendar();

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const today = new Date();
    today.setDate(1);
    today.setHours(0, 0, 0, 0);
    
    if (newMonth >= today) {
      setCurrentMonth(newMonth);
      // Clear selected date and time when changing months
      setSelectedDate(null);
      setSelectedTime(null);
    }
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    // Clear selected date and time when changing months
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const canGoPrevious = () => {
    const today = new Date();
    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return currentMonth > thisMonth;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset time when date changes
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleConfirmBooking = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Parse first and last name from full name
      const nameParts = formData.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const bookingData = {
        firstName: firstName,
        lastName: lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        zip: formData.zip,
        date: selectedDate,
        time: selectedTime,
        duration: selectedService.duration,
        serviceId: selectedService.id,
        providerId: selectedProvider.id,
        notes: `Booking frequency: ${frequency}`,
        location: formData.address,
      };

      const result = await apiService.bookAppointment(bookingData);
      setBookingResult(result);
      setBookingComplete(true);
    } catch (err) {
      setError('Failed to complete booking. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const canProceedToStep3 = selectedDate && selectedTime;

  const getPrice = () => {
    if (!selectedService) return 0;
    return selectedService.price;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${mins} minutes`;
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center transform animate-fade-in">
            <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Booking Confirmed!</h2>
            <p className="text-xl text-gray-600 mb-8">
              Your spotless space is on its way. We've sent a confirmation to {formData.email}
            </p>
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Booking Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-semibold text-gray-900">{selectedService.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Provider:</span>
                  <span className="font-semibold text-gray-900">
                    {selectedProvider.firstName} {selectedProvider.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-semibold text-gray-900">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-semibold text-gray-900">{selectedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold text-gray-900">{formatDuration(selectedService.duration)}</span>
                </div>
                {bookingResult && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <span className="font-semibold text-gray-900">{bookingResult.appointment.hash}</span>
                  </div>
                )}
                <div className="border-t pt-3 mt-3 flex justify-between">
                  <span className="text-gray-900 font-bold">Total:</span>
                  <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                    ${getPrice()} {selectedService.currency || ''}
                  </span>
                </div>
              </div>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition-all transform hover:scale-105"
            >
              Book Another Service
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header */}
      <TopHeader />

      {/* Error Alert */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                  currentStep >= step 
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                </div>
                <span className={`mt-2 text-sm font-medium ${
                  currentStep >= step ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step === 1 ? 'Choose Service' : step === 2 ? 'Select Date & Time' : 'Confirm'}
                </span>
              </div>
              {step < 3 && (
                <div className={`flex-1 h-1 mx-4 rounded transition-all ${
                  currentStep > step ? 'bg-gradient-to-r from-purple-600 to-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="text-gray-700 font-semibold">Loading...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Step 1: Choose Service */}
        {currentStep === 1 && (
          <div className="animate-fade-in">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Choose Your Service</h2>
            <p className="text-center text-gray-600 mb-8">Select the cleaning service that fits your needs</p>

            {/* Service Cards */}
            {services.length === 0 && !isLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No services available. Please check your internet connection.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    className={`bg-white rounded-2xl p-6 cursor-pointer transition-all transform hover:scale-105 hover:shadow-2xl ${
                      selectedService?.id === service.id
                        ? 'ring-4 ring-purple-500 shadow-2xl'
                        : 'shadow-lg'
                    }`}
                  >
                    <div className="text-5xl mb-4">{ service.extra.icon }</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{service.extra.description}</p>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                        ${service.price}
                      </span>
                      <span className="text-gray-600 text-sm">{service.currency || ''}</span>
                    </div>
                    <p className="text-sm text-gray-500">~{formatDuration(service.duration)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Date & Time */}
        {currentStep === 2 && (
          <div className="animate-fade-in">
            <button
              onClick={() => setCurrentStep(1)}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800 mb-6 font-semibold"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Services
            </button>

            <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Select Date & Time</h2>
            <p className="text-center text-gray-600 mb-8">Choose when you'd like us to arrive</p>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Date Selection */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-purple-600" />
                    <h3 className="text-xl font-bold text-gray-900">Choose Date</h3>
                    {checkingDates && (
                      <div className="ml-2 flex items-center gap-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                        <span>Checking availability...</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={goToPreviousMonth}
                      disabled={!canGoPrevious()}
                      className={`p-2 rounded-lg transition-all ${
                        canGoPrevious()
                          ? 'hover:bg-gray-100 text-gray-700'
                          : 'text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="font-semibold text-gray-900 min-w-[140px] text-center">
                      {formatMonthYear(currentMonth)}
                    </span>
                    <button
                      onClick={goToNextMonth}
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-700 transition-all"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Calendar Grid */}
                <div className="mb-3">
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {calendar.map((date, index) => {
                      if (!date) {
                        return <div key={index} className="aspect-square" />;
                      }
                      
                      const isSelected = selectedDate?.toDateString() === date.toDateString();
                      const isUnavailable = unavailableDates.has(date.toDateString());
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      const dayNumber = date.getDate();
                      
                      return (
                        <button
                          key={index}
                          onClick={() => !isUnavailable && handleDateSelect(date)}
                          disabled={isUnavailable}
                          className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg transform scale-110'
                              : isUnavailable
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-50'
                              : 'bg-gray-50 hover:bg-gray-100 text-gray-900 hover:scale-105'
                          }`}
                          title={isUnavailable ? 'Not available' : 'Available'}
                        >
                          <span className={`text-lg font-semibold ${isSelected ? 'text-white' : isUnavailable ? 'text-gray-300' : ''}`}>
                            {dayNumber}
                          </span>
                          {isWeekend && !isSelected && !isUnavailable && (
                            <span className="text-[10px] text-purple-600">•</span>
                          )}
                          {isUnavailable && (
                            <span className="text-[10px] text-red-400 mt-0.5">✕</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Calendar Legend */}
                <div className="flex items-center justify-center gap-4 text-xs text-gray-600 mt-4 pt-4 border-t">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-gray-50 border border-gray-200"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200"></div>
                    <span>Unavailable</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-gradient-to-r from-purple-600 to-blue-600"></div>
                    <span>Selected</span>
                  </div>
                </div>
              </div>

              {/* Time Selection */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-6 h-6 text-purple-600" />
                  <h3 className="text-xl font-bold text-gray-900">Choose Time</h3>
                </div>

                {!selectedDate ? (
                  <div className="text-center py-12 text-gray-500">
                    Please select a date first
                  </div>
                ) : availableTimeSlots.length === 0 && !isLoading ? (
                  <div className="text-center py-12 text-gray-500">
                    No available time slots for this date
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {availableTimeSlots.map((slot, index) => (
                      <button
                        key={index}
                        onClick={() => slot.available && handleTimeSelect(slot.time)}
                        disabled={!slot.available}
                        className={`w-full p-4 rounded-xl text-left transition-all ${
                          selectedTime === slot.time
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                            : slot.available
                            ? 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{slot.time}</span>
                          {!slot.available && (
                            <span className="text-sm">Unavailable</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {selectedService && (
                  <div className="mt-6 p-4 bg-purple-50 rounded-xl">
                    <p className="text-sm text-gray-600">Estimated Duration</p>
                    <p className="font-semibold text-gray-900">{formatDuration(selectedService.duration)}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={() => setCurrentStep(3)}
                disabled={!canProceedToStep3}
                className={`px-8 py-4 rounded-full text-lg font-semibold flex items-center gap-2 transition-all transform ${
                  canProceedToStep3
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-2xl hover:scale-105'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue to Confirmation
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm Booking */}
        {currentStep === 3 && (
          <div className="animate-fade-in">
            <button
              onClick={() => setCurrentStep(2)}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800 mb-6 font-semibold"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Date & Time
            </button>

            <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Confirm Your Booking</h2>
            <p className="text-center text-gray-600 mb-8">Just a few more details and you're all set!</p>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Booking Summary */}
              <div className="bg-white rounded-2xl p-8 shadow-lg h-fit">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Booking Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 pb-4 border-b">
                    <div className="text-4xl"> { selectedService.extra.icon } </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{selectedService.name}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2">{selectedService.extra.description}</p>
                      <p className="text-sm text-purple-600 font-medium mt-1">
                        {formatDuration(selectedService.duration)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <User className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Provider</p>
                      <p className="font-semibold text-gray-900">
                        {selectedProvider.firstName} {selectedProvider.lastName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-semibold text-gray-900">{formatDate(selectedDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Time</p>
                      <p className="font-semibold text-gray-900">{selectedTime}</p>
                    </div>
                  </div>
                  <div className="pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold text-gray-900">${getPrice()}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-600">Service Fee</span>
                      <span className="font-semibold text-gray-900">$0</span>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-4 border-t">
                      <span className="text-xl font-bold text-gray-900">Total</span>
                      <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                        ${getPrice()} {selectedService.currency || ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Information</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <User className="w-4 h-4" />
                      Full Name
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Mail className="w-4 h-4" />
                      Email Address
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Phone className="w-4 h-4" />
                      Phone Number
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <MapPin className="w-4 h-4" />
                      Service Address
                    </div>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main Street, Ottawa, ON"
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all resize-none"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <MapPin className="w-4 h-4" />
                      City
                    </div>
                    <select 
                      name='city' 
                      className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all'
                      value={formData.city} 
                      onChange={handleInputChange} 
                      >
                      <option value="ottawa" >Ottawa</option>
                      <option value="gatineau" >Gatineau</option>
                    </select>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={handleConfirmBooking}
                      disabled={!formData.name || !formData.email || !formData.address || isLoading || !formData.phone}
                      className={`w-full py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 transition-all transform ${
                        formData.name && formData.phone && formData.email && formData.address && !isLoading
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-2xl hover:scale-105'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5" />
                          Confirm Booking
                        </>
                      )}
                    </button>
                    <p className="text-xs text-center text-gray-500 mt-3">
                      Payment will be processed after service completion
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}